// ── Metric definitions registry ───────────────────────────────────────────
// The single source of truth for "what every number on the dashboard means."
// Every metric shown in the UI should have an entry here, and every metric
// label should sit next to an infoIcon('<key>') (see shared/components/InfoIcon).
//
// This is the backbone of the explanation layer (Phase 1 of the redesign). It
// mirrors wchdash's metric_definitions.py. Add a key here, reference it with
// infoIcon('key'), and the glossary + tooltip modal pick it up automatically.

export interface MetricDef {
  key: string;
  term: string;
  category: MetricCategory;
  what: string;   // plain-English: what this number is
  why: string;    // why it matters / how to read it
  how: string;    // how it's computed
  related?: string[]; // related metric keys
}

export type MetricCategory =
  | 'Revenue'
  | 'Pacing & Pace'
  | 'Market'
  | 'Health & Risk'
  | 'Pricing'
  | 'Financials'
  | 'Opportunity';

export const METRIC_DEFS: Record<string, MetricDef> = {
  // ── Revenue ──
  occupancy: {
    key: 'occupancy', term: 'Occupancy', category: 'Revenue',
    what: 'The share of available nights that are booked in a period.',
    why: 'The core demand signal — low occupancy with lead time left means there is room to win bookings; high occupancy may mean you are leaving money on the table by underpricing.',
    how: 'booked nights ÷ total available nights, counting each night once even when reservations overlap. Owner-blocked nights are excluded from "available".',
    related: ['adr', 'revpar', 'mpi'],
  },
  adr: {
    key: 'adr', term: 'ADR (Average Daily Rate)', category: 'Revenue',
    what: 'The average nightly rate actually earned on booked nights.',
    why: 'Tells you the price you are realizing. Compare to market ADR to see if you are priced above or below the market.',
    how: 'booked-night revenue ÷ booked nights.',
    related: ['revpar', 'all_in_price', 'occupancy'],
  },
  revpar: {
    key: 'revpar', term: 'RevPAR (Revenue per Available Night)', category: 'Revenue',
    what: 'Revenue earned per available night — the blend of how full you are and how much you charge.',
    why: 'The single best yield number: it captures occupancy and ADR together, so you can compare properties of different sizes and seasons fairly.',
    how: 'revenue ÷ total available nights (= occupancy × ADR).',
    related: ['revpar_index', 'occupancy', 'adr'],
  },
  gross_revenue: {
    key: 'gross_revenue', term: 'Gross Booking Revenue', category: 'Revenue',
    what: 'Total guest-paid revenue on a reservation before any splits.',
    why: 'The top line. Owner payout, management fee, and taxes are all carved out of this.',
    how: 'Sum of guest-facing line items (rent + cleaning + fees), excluding pass-through taxes the company never keeps.',
    related: ['owner_net', 'management_fee', 'commissionable_revenue'],
  },
  commissionable_revenue: {
    key: 'commissionable_revenue', term: 'Commissionable Revenue', category: 'Revenue',
    what: 'The portion of revenue the management fee percentage is applied to.',
    why: 'Different properties commission different things (some include cleaning, some do not), so this is the fair base for the fee.',
    how: 'Sum of line items the property\'s formula marks "commissionable", optionally net of channel fees.',
    related: ['management_fee', 'gross_revenue'],
  },
  management_fee: {
    key: 'management_fee', term: 'Management Fee', category: 'Revenue',
    what: 'What the company earns managing the property for a reservation.',
    why: 'The company\'s primary revenue. Drives profitability and is what you report to owners.',
    how: 'commission rate × commissionable base, with a per-booking minimum floor if the formula sets one.',
    related: ['commissionable_revenue', 'owner_net', 'blended_rate'],
  },
  owner_net: {
    key: 'owner_net', term: 'Owner Net', category: 'Revenue',
    what: 'What the property owner receives after fees, channel costs, and taxes.',
    why: 'The number owners care about most. It is what you reconcile on the owner statement.',
    how: 'revenue we collect − channel fees − (management fee + company pass-through).',
    related: ['management_fee', 'gross_revenue', 'channel_fees'],
  },
  channel_fees: {
    key: 'channel_fees', term: 'Channel Fees', category: 'Revenue',
    what: 'Fees the booking channel (e.g. Airbnb) takes out of a reservation.',
    why: 'A real cost that reduces what the owner nets; some formulas subtract it before computing the management fee.',
    how: 'Sum of channel-cost line items (stored as a positive cost).',
    related: ['owner_net'],
  },
  blended_rate: {
    key: 'blended_rate', term: 'Blended Take Rate', category: 'Revenue',
    what: 'The effective percentage of gross revenue the company keeps across all fee types.',
    why: 'A quick read on how much value the company captures per dollar booked.',
    how: 'total company revenue ÷ gross booking revenue.',
    related: ['management_fee', 'gross_revenue'],
  },
  booked_nights: {
    key: 'booked_nights', term: 'Booked Nights', category: 'Revenue',
    what: 'The total number of nights sold across reservations in a period.',
    why: 'The volume side of revenue — paired with ADR it explains how a property earned what it did.',
    how: 'Sum of nights between check-in and check-out across non-cancelled reservations.',
    related: ['adr', 'occupancy', 'reservation_count'],
  },
  reservation_count: {
    key: 'reservation_count', term: 'Reservations', category: 'Revenue',
    what: 'The count of bookings in a period.',
    why: 'A demand-volume signal — many short bookings vs. a few long ones is a very different mix at the same revenue.',
    how: 'Count of non-cancelled reservations whose check-in falls in the period.',
    related: ['booked_nights', 'gross_revenue'],
  },

  // ── Pacing & Pace ──
  goal: {
    key: 'goal', term: 'Revenue Goal', category: 'Pacing & Pace',
    what: 'The target revenue for a property-month.',
    why: 'The yardstick for pacing. Actual ÷ goal tells you if you are on plan.',
    how: 'Operator override if set; otherwise an auto-projection blending last year (YoY-adjusted) with market RevPAR × nights × the listing\'s premium.',
    related: ['projection', 'pace_vs_goal', 'pace_vs_stly'],
  },
  projection: {
    key: 'projection', term: 'Auto Projection', category: 'Pacing & Pace',
    what: 'The model\'s estimate of where a month will land.',
    why: 'Gives a goal even for properties without a manual target, anchored to the market.',
    how: 'Blend of last-year revenue × (1 + clamped YoY growth) and market p50 RevPAR × nights × listing premium (trailing-90d MPI), weighted by booking density.',
    related: ['goal', 'mpi'],
  },
  pace_vs_goal: {
    key: 'pace_vs_goal', term: 'Pace vs Goal', category: 'Pacing & Pace',
    what: 'How far through the month\'s goal you already are.',
    why: '100% means exactly on plan. Below means behind; above means ahead.',
    how: 'actual (+ on-the-books) ÷ goal × 100.',
    related: ['goal', 'pace_vs_stly'],
  },
  pace_vs_stly: {
    key: 'pace_vs_stly', term: 'Pace vs STLY', category: 'Pacing & Pace',
    what: 'Where you are on the books now vs. the same time last year (STLY = same time last year).',
    why: 'The truest read of momentum — it compares like-for-like booking windows, not final results. Ahead-of-STLY means demand is stronger than last year at this point.',
    how: 'on-the-books revenue for the period today ÷ on-the-books revenue captured by the same calendar day last year. Requires weekly book snapshots.',
    related: ['pace_vs_goal', 'pickup'],
  },
  pickup: {
    key: 'pickup', term: 'Pickup', category: 'Pacing & Pace',
    what: 'New bookings made recently (e.g. last 24h / 7d / 30d), regardless of stay date.',
    why: 'Measures booking momentum right now — a spike or drought is an early signal before occupancy moves.',
    how: 'Count and revenue of reservations whose booking date falls in the window.',
    related: ['pace_vs_stly'],
  },

  // ── Market ──
  mpi: {
    key: 'mpi', term: 'MPI (Market Penetration Index)', category: 'Market',
    what: 'Your occupancy relative to the market\'s occupancy.',
    why: 'Above 1.0 means you are filling more than your fair share; below means you are losing demand to comparable listings.',
    how: 'your occupancy ÷ market occupancy at your chosen comp percentile.',
    related: ['revpar_index', 'comp_percentile'],
  },
  revpar_index: {
    key: 'revpar_index', term: 'RevPAR Index', category: 'Market',
    what: 'Your RevPAR relative to the market\'s RevPAR.',
    why: 'The yield version of MPI: above 1.0 means you out-earn comparable listings per available night.',
    how: 'your RevPAR ÷ market RevPAR at your chosen comp percentile.',
    related: ['mpi', 'comp_percentile', 'revpar'],
  },
  comp_percentile: {
    key: 'comp_percentile', term: 'Comp Percentile', category: 'Market',
    what: 'Which tier of the local market you are measured against (p25 conservative → p90 aggressive).',
    why: 'Sets the bar. A property competing with top listings should be measured at p75–p90; a value listing at p25–p50.',
    how: 'Operator-chosen percentile (default p50); the system can suggest the tier that best matches the property\'s trailing-12-month RevPAR.',
    related: ['mpi', 'revpar_index'],
  },
  market_adr: {
    key: 'market_adr', term: 'Market ADR', category: 'Market',
    what: 'The average nightly rate for comparable listings in the local market.',
    why: 'The benchmark for your own ADR and all-in price — tells you if you are priced with the market.',
    how: 'From AirROI market data at your chosen comp percentile.',
    related: ['adr', 'all_in_price'],
  },

  // ── Health & Risk ──
  health_score: {
    key: 'health_score', term: 'Listing Health Score', category: 'Health & Risk',
    what: 'A 0–100 score of how on-track a listing\'s open calendar is.',
    why: 'A fast triage signal — low scores mean many upcoming nights are at risk of going unbooked or are mispriced.',
    how: 'Share of open future nights whose booking probability sits inside a healthy, lead-time-aware band; deviations are weighted more heavily the worse they are.',
    related: ['p_book', 'underbooked', 'underpriced'],
  },
  quality_score: {
    key: 'quality_score', term: 'Quality Score', category: 'Health & Risk',
    what: 'A 0–100 score of the asset itself: reviews (level, trend, recent bad ones), channel coverage, min-stay, price floor, and whether price-push automation is on.',
    why: 'Quality problems compound slowly and quietly — a slipping review average or a missing channel costs bookings for months before it shows in revenue.',
    how: 'Starts at 100; each open quality flag subtracts a severity penalty (high −25, warn −12, note −4). Same 80/60/40 color bands as every other score.',
    related: ['performance_score', 'combined_score', 'review_score'],
  },
  performance_score: {
    key: 'performance_score', term: 'Performance Score', category: 'Health & Risk',
    what: 'A 0–100 score of how the listing\'s revenue is pacing: occupancy vs comp set, pace vs same time last year, fillable gaps, pickup momentum, MPI, and trailing revenue vs last year.',
    why: 'Performance problems are the fix-the-price-today bucket — they move bookings this month, not next quarter.',
    how: 'Starts at 100; each open performance flag subtracts a severity penalty (high −25, warn −12, note −4).',
    related: ['quality_score', 'combined_score', 'mpi', 'pace_vs_stly'],
  },
  combined_score: {
    key: 'combined_score', term: 'Combined Score', category: 'Health & Risk',
    what: 'The single triage number: 40% quality score + 60% performance score.',
    why: 'Performance weighs heavier because pricing moves bookings now; quality compounds over time. The Triage queue sorts worst-first on this.',
    how: '0.4 × quality + 0.6 × performance, rounded. Done/Snooze/Dismiss on a flag recomputes it immediately.',
    related: ['quality_score', 'performance_score'],
  },
  smart_gaps: {
    key: 'smart_gaps', term: 'Fillable Gaps', category: 'Health & Risk',
    what: 'Short holes you can realistically sell: 1–4 open nights wedged between bookings (or starting within a week) inside the next 45 days.',
    why: 'A random Tue–Thu hole between two stays is found money — a price nudge or a min-stay exception usually fills it. Long open runs are NOT gaps; they\'re pacing problems and flag as those.',
    how: 'Scanned from the forward calendar. Estimated missed revenue = gap nights × the listing\'s recent ADR; severity rides the gap count (4+ = a calendar-management pattern).',
    related: ['performance_score', 'adr'],
  },
  p_book: {
    key: 'p_book', term: 'Booking Probability', category: 'Health & Risk',
    what: 'The estimated chance a given open night gets booked before arrival.',
    why: 'Drives risk flags. A low probability with lots of lead time left is an opportunity to act (promo, min-stay, price).',
    how: 'Heuristic today (comp-percentile occupancy × historical lead-time booking curve); a calibrated ML model in a later phase.',
    related: ['health_score', 'underbooked'],
  },
  underbooked: {
    key: 'underbooked', term: 'Underbooked', category: 'Health & Risk',
    what: 'An open night whose booking probability is below the healthy band.',
    why: 'Demand-side risk — the lever is visibility/promo/min-stay or a lower rate.',
    how: 'P(book) < the lead-time-aware healthy band\'s lower edge.',
    related: ['underpriced', 'p_book'],
  },
  underpriced: {
    key: 'underpriced', term: 'Underpriced', category: 'Health & Risk',
    what: 'An open night whose booking probability is above the healthy band — likely to book even at a higher rate.',
    why: 'Money left on the table — the lever is to raise the rate.',
    how: 'P(book) > the lead-time-aware healthy band\'s upper edge.',
    related: ['underbooked', 'p_book'],
  },

  // ── Pricing ──
  all_in_price: {
    key: 'all_in_price', term: 'All-in Nightly Price', category: 'Pricing',
    what: 'The true guest-facing per-night price including cleaning, fees, tax, and channel markup.',
    why: 'The only fair way to compare your price to market ADR — base rate alone understates what guests actually pay.',
    how: 'base × length-of-stay × (1 + channel markup) + cleaning + mandatory fees, taxed, then divided back to a per-night figure using a blended length-of-stay.',
    related: ['adr', 'market_adr'],
  },

  // ── Risk / Local demand ──
  rule_flag: {
    key: 'rule_flag', term: 'Rule Flag', category: 'Health & Risk',
    what: 'An automated alert raised by a daily rule when a property-night trips a risk threshold.',
    why: 'The inbox of "look at this today" items — each flag points at a specific property and stay date with the gap that triggered it.',
    how: 'A daily job checks every open property-night against fixed rules (PriceLabs vs. published price gap, unbooked weekends inside 21 days), deduped per day; medium+ flags also Slack.',
    related: ['price_gap', 'underpriced'],
  },
  price_gap: {
    key: 'price_gap', term: 'Price Gap', category: 'Pricing',
    what: 'How far the published nightly price sits from the PriceLabs recommended price for a stay date.',
    why: 'A large gap is the clearest pricing action — under means you are leaving money on the table, over means you may be pricing yourself out of the booking.',
    how: 'absolute(recommended − published) ÷ recommended, bucketed into low / medium / high severity.',
    related: ['rule_flag', 'all_in_price', 'underpriced'],
  },
  event_magnitude: {
    key: 'event_magnitude', term: 'Event Magnitude', category: 'Market',
    what: 'The estimated demand impact of a nearby Ticketmaster event (none / small / medium / large / major).',
    why: 'Medium+ events pull extra demand into the local market on their date — a cue to hold or raise rates and tighten min-stay around them.',
    how: 'Scored from venue capacity, sport/music classification, price tier, and attendance; only medium+ events surface as markers.',
    related: ['market_adr', 'occupancy'],
  },

  // ── Opportunity ──
  airroi_opportunity: {
    key: 'airroi_opportunity', term: 'AirROI Opportunity', category: 'Opportunity',
    what: 'Estimated annual revenue upside vs. the market\'s expectation for a comparable listing.',
    why: 'Sizes the prize per property so you can focus on the biggest wins.',
    how: 'AirROI market revenue estimate at the comp percentile minus the property\'s projected revenue.',
    related: ['revpar_index', 'comp_percentile'],
  },
  gap_nights: {
    key: 'gap_nights', term: 'Gap Nights', category: 'Opportunity',
    what: 'Open, bookable nights between reservations that are neither booked nor owner-blocked.',
    why: 'Concrete, fillable inventory — the most actionable form of lost revenue.',
    how: 'Future nights with no reservation and no owner block, collapsed into windows.',
    related: ['missed_revenue'],
  },
  missed_revenue: {
    key: 'missed_revenue', term: 'Missed Revenue', category: 'Opportunity',
    what: 'The revenue a gap window would earn if filled.',
    why: 'Prioritizes which gaps to chase first.',
    how: 'gap nights × the date-aware ADR (the gap\'s own nightly prices, else market ADR).',
    related: ['gap_nights'],
  },
  captured_revenue: {
    key: 'captured_revenue', term: 'Captured Revenue', category: 'Opportunity',
    what: 'Revenue honestly attributable to a revenue-management action after the stay date passes.',
    why: 'The ROI proof — what acting actually earned, with no counterfactual hand-waving.',
    how: 'For an accepted price drop that booked: realized rent × the booking risk it carried; for an accepted raise that booked: the price increase × the chance it would have booked anyway. Missed actions count $0.',
    related: ['management_fee'],
  },

  // ── Financials ──
  noi: {
    key: 'noi', term: 'Net Operating Income (per property)', category: 'Financials',
    what: 'A property\'s profit after direct expenses and its share of overhead.',
    why: 'The real bottom line per property, beyond top-line revenue.',
    how: 'income − direct expenses − allocated overhead (from QuickBooks).',
    related: ['expense_ratio'],
  },
  expense_ratio: {
    key: 'expense_ratio', term: 'Expense Ratio', category: 'Financials',
    what: 'Operating expenses as a share of revenue.',
    why: 'Shows how much of each revenue dollar is consumed by operations.',
    how: 'operating expenses ÷ revenue.',
    related: ['noi'],
  },
  operating_expense: {
    key: 'operating_expense', term: 'Operating Expense', category: 'Financials',
    what: 'Money spent running a property — cleaning, maintenance, supplies, and other operational costs.',
    why: 'Direct drag on owner net and NOI; tracking it per property surfaces where margin is leaking.',
    how: 'Sum of expense line items synced from the PMS for the property and period.',
    related: ['expense_ratio', 'noi', 'task_cost'],
  },
  task_cost: {
    key: 'task_cost', term: 'Task Cost', category: 'Financials',
    what: 'The labor/vendor cost attached to operational tasks (cleaning, maintenance, etc.).',
    why: 'A component of operating expense; rising task cost without more bookings squeezes margin.',
    how: 'Sum of the cost field on synced tasks for the period.',
    related: ['operating_expense', 'expense_ratio'],
  },
  // R110 #2: the work-order editor's "Payer" and "Billed" read as one idea to the
  // office (Jesse's screenshot) — they are two different questions. Written for a
  // new office hire. Stored values are untouched: payer 'wch'|'owner';
  // billing_status ''|'billed_owner'|'billed_os' ("OS" = the owner's monthly
  // statement, which is generated externally in VR Platform).
  wo_payer: {
    key: 'wo_payer', term: 'Who pays (work-order cost)', category: 'Financials',
    what: 'Whose money covers a work order\'s cost: WCH (it comes out of West Coast Homestays\' pocket) or Owner (it is the owner\'s expense and they reimburse us). It says nothing about whether anyone has been charged yet — that is "Billed yet?".',
    why: 'This is the field that actually moves numbers: the Costs tab splits spend by payer, the owner queue asks "who pays?" before publishing, and owner-pays work shows up as an expense in the owner portal and the care report. Get it wrong and the owner sees a charge that was ours — or we quietly eat one that was theirs.',
    how: 'Set by hand on each work order (Maintenance → Money & billing → Who pays) or on Supplies & Expenses. WCH = we absorb it; Owner = the owner reimburses us. Marking something billed never changes who pays, and a cost recorded here never lands on the owner\'s monthly statement by itself — that statement is built separately in VR Platform.',
    related: ['wo_billed', 'task_cost', 'operating_expense'],
  },
  wo_billed: {
    key: 'wo_billed', term: 'Billed yet? (work-order cost)', category: 'Financials',
    what: 'Michelle\'s tracker for an owner-pays cost: has the owner been charged for it yet, and how? "Not billed yet" = still to do. "Billed · invoiced to owner" = we sent the owner an invoice directly. "Billed · on owner statement" = it was added as a line on the owner\'s monthly statement.',
    why: 'Only meaningful when the owner pays — it is the to-do list that stops owner expenses being forgotten, or charged twice (an invoice AND a statement line). It is a bookkeeping note: no total, report or portal number changes when it flips.',
    how: 'Set by hand in the work-order editor or on Supplies & Expenses and stored as the work order\'s billing status. The board\'s "Billed yet?" filter and the CSV export read it; nothing is calculated from it. It does not push anything onto the owner statement — that is still generated externally in VR Platform; this only records that it was done.',
    related: ['wo_payer', 'task_cost'],
  },

  // ── Reviews ──
  avg_rating: {
    key: 'avg_rating', term: 'Average Rating', category: 'Health & Risk',
    what: 'The mean guest review score for a property, out of 5.0.',
    why: 'A core quality and trust signal — higher ratings lift conversion and let you hold rate; falling ratings precede demand loss.',
    how: 'Average of published guest review ratings.',
    related: ['response_rate'],
  },
  response_rate: {
    key: 'response_rate', term: 'Response Rate', category: 'Health & Risk',
    what: 'The share of guest reviews that have received a host response.',
    why: 'Responding to reviews — especially low ones — signals attentive management to future guests and the channel algorithms.',
    how: 'reviews with a host reply ÷ total reviews.',
    related: ['avg_rating'],
  },
  revenue_band: {
    key: 'revenue_band', term: 'Annual Revenue vs Market', category: 'Market',
    what: 'Where your property\'s projected annual revenue sits inside the AirROI revenue range for comparable listings — the bar spans the 25th percentile (a weak comp) to the 90th (top of market).',
    why: 'Sizes the gap to the market in dollars: if your run-rate (the box) sits left of the expected line, similar listings are out-earning you — that difference is recoverable revenue, and the whole point of acting on this property.',
    how: 'AirROI provides the annual-revenue distribution for similar listings; the bar runs p25→p90. The blue line │ is AirROI\'s expected revenue for your comp tier; the box ◻︎ is your property\'s own projected run-rate (ADR × occupancy annualized from on-the-books + pace). Left of the line = under-earning.',
    related: ['airroi_opportunity', 'revpar_index', 'comp_percentile'],
  },
};

export function getMetricDef(key: string): MetricDef | null {
  return METRIC_DEFS[key] || null;
}

export function metricsByCategory(): Record<string, MetricDef[]> {
  const out: Record<string, MetricDef[]> = {};
  for (const def of Object.values(METRIC_DEFS)) {
    (out[def.category] ||= []).push(def);
  }
  return out;
}
